// 라이트 테마 색상 상수
const COLORS = {
  // 배경색
  background: '#ffffff',
  backgroundSecondary: '#f8f9fa',
  backgroundTertiary: '#f1f3f5',

  // 사이드바
  sidebarBg: '#ffffff',
  sidebarBorder: '#e9ecef',

  // 텍스트
  textPrimary: '#333333',
  textSecondary: '#666666',
  textTertiary: '#999999',

  // 액센트
  primary: '#3B82F6',
  primaryHover: '#2563EB',
  primaryLight: 'rgba(59, 130, 246, 0.1)',

  // 상태 색상
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',

  // 테두리
  border: '#e2e8f0',
  borderLight: '#f1f3f5',

  // 그림자
  shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.07)',
  shadowLg: '0 10px 15px rgba(0, 0, 0, 0.1)'
};

export const createAppStyles = (sidebarWidth, sidebarOpen, isMobile) => ({
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: COLORS.backgroundSecondary,
    color: COLORS.textPrimary
  },

  // 사이드바 - 라이트 테마, position fixed, 20% 너비 (모바일: 전체화면)
  sidebar: {
    width: isMobile ? (sidebarOpen ? '100vw' : '0') : '20vw',
    minWidth: isMobile ? (sidebarOpen ? '100vw' : '0') : '270px',
    maxWidth: isMobile ? (sidebarOpen ? '100vw' : '0') : '20vw',
    backgroundColor: COLORS.sidebarBg,
    borderRight: isMobile ? 'none' : `1px solid ${COLORS.sidebarBorder}`,
    display: 'flex',
    flexDirection: 'column',
    color: COLORS.textPrimary,
    transition: 'width 0.3s ease, min-width 0.3s ease',
    overflow: 'hidden',
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100dvh',  // 실제 렌더링되는 viewport 기준 (모바일 주소창 등 고려)
    zIndex: 200,
    boxShadow: COLORS.shadowMd
  },

  // 모바일 사이드바 닫기 버튼
  sidebarCloseButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.textSecondary,
    fontSize: '28px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 101,
    transition: 'all 0.2s ease'
  },

  // 메인 콘텐츠 - 사이드바 너비만큼 margin-left (20%, 최소 300px)
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: COLORS.backgroundSecondary,
    marginLeft: isMobile ? 0 : 'max(20vw, 270px)',
    transition: 'margin-left 0.3s ease',
    minHeight: '100vh'
  },

  // 로고 영역 - 패딩 2배 (20px→40px, 24px→48px)
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '40px 48px',
    borderBottom: `1px solid ${COLORS.sidebarBorder}`,
    flexShrink: 0
  },

  // 로고 이미지 - 크기 2배 (36px→72px)
  logoImage: {
    width: 'auto',
    height: '72px'
  },

  // 네비게이션 메뉴
  navMenu: {
    flex: 1,
    padding: '24px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',  // 기존 4px에서 8px로 (2배)
    overflow: 'auto'  // 메뉴가 많으면 스크롤
  },

  navItem: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',  // 상하 패딩 증가
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: active ? COLORS.primaryLight : 'transparent',
    color: active ? COLORS.primary : COLORS.textSecondary,
    fontWeight: active ? '600' : '500',
    fontSize: '22px',  // 22px로 변경
    transition: 'all 0.2s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left'
  }),

  navIcon: {
    width: '20px',
    height: '20px',
    objectFit: 'contain'
  },

  // 하위 메뉴
  subMenu: {
    marginLeft: '32px',
    marginTop: '8px',  // 기존 4px에서 8px로 (2배)
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'  // 기존 2px에서 4px로 (2배)
  },

  subMenuItem: (active) => ({
    padding: '12px 16px',  // 상하 패딩 증가
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: active ? COLORS.primaryLight : 'transparent',
    color: active ? COLORS.primary : COLORS.textTertiary,
    fontWeight: active ? '500' : '400',
    fontSize: '18px',  // 18px로 변경
    transition: 'all 0.2s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left'
  }),

  // 사용자 프로필 영역
  userProfile: {
    padding: '20px 24px',
    borderTop: `1px solid ${COLORS.sidebarBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    marginTop: 'auto'  // 항상 사이드바 하단에 고정
  },

  // 사용자 정보 (아이콘 + 이름)
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },

  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    objectFit: 'cover'
  },

  userName: {
    fontSize: '20px',
    fontWeight: '600',
    color: COLORS.textPrimary,
    paddingLeft: '8px'
  },

  logoutButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '12px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease'
  },

  logoutIcon: {
    width: '30px',    // 20px * 2.5 = 50px
    height: '30px',   // 20px * 2.5 = 50px
    opacity: 0.6
  },

  // 헤더 - 라이트 테마
  header: {
    backgroundColor: COLORS.background,
    borderBottom: `1px solid ${COLORS.border}`,
    padding: isMobile ? '12px 12px' : '20px 32px',
    display: 'flex',
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'space-between',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '12px' : '0'
  },

  // 페이지 제목 - 32px로 변경
  pageTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: COLORS.textPrimary,
    margin: 0
  },

  // 부제목 - 22px로 변경
  pageSubtitle: {
    fontSize: '18px',
    color: COLORS.textSecondary,
    margin: '4px 0 0 0'
  },

  // 탭 컨테이너
  tabContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    backgroundColor: COLORS.backgroundTertiary,
    padding: isMobile ? '6px' : '4px',
    borderRadius: isMobile ? '8px' : '10px',
    flexWrap: 'nowrap',
    width: isMobile ? 'auto' : 'auto',
    justifyContent: 'flex-start',
    flexShrink: isMobile ? 0 : 'unset'
  },

  // 탭 버튼 - pill 형태 (모바일: 적당한 크기)
  pptTabButton: (active) => ({
    backgroundColor: active ? COLORS.primary : 'transparent',
    color: active ? '#ffffff' : COLORS.textSecondary,
    border: 'none',
    borderRadius: isMobile ? '6px' : '8px',
    padding: isMobile ? '12px 12px' : '12px 12px',
    cursor: 'pointer',
    fontSize: isMobile ? '14px' : '14px',
    fontWeight: '700',
    transition: 'all 0.2s ease',
    minWidth: isMobile ? 'auto' : '100px',
    whiteSpace: 'nowrap'
  }),

  // 카드 스타일
  card: {
    backgroundColor: COLORS.background,
    borderRadius: '16px',
    boxShadow: COLORS.shadow,
    padding: '24px',
    border: `1px solid ${COLORS.borderLight}`
  },

  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.textPrimary,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  cardSubtitle: {
    fontSize: '13px',
    color: COLORS.textTertiary,
    margin: '4px 0 0 0'
  },

  cardIcon: {
    width: '24px',
    height: '24px'
  },

  // 버튼 스타일
  button: {
    backgroundColor: COLORS.primary,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background-color 0.2s ease',
    boxShadow: COLORS.shadow
  },

  buttonSecondary: {
    backgroundColor: COLORS.background,
    color: COLORS.textPrimary,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '8px',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease'
  },

  // 삭제 버튼
  deleteButton: {
    backgroundColor: COLORS.error,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
  },

  // 크레딧 배지
  creditBadge: (credit) => ({
    backgroundColor: credit > 500 ? COLORS.success : credit > 100 ? COLORS.warning : COLORS.error,
    color: 'white',
    borderRadius: '20px',
    padding: isMobile ? '4px 12px' : '6px 16px',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    boxShadow: COLORS.shadow
  }),

  // ===== 기존 호환성 유지를 위한 스타일 =====

  // 업로드 섹션 (프로젝트 화면에서 사용)
  uploadSection: {
    padding: '24px',
    flex: 1,
    background: COLORS.background,
    overflowY: 'auto',
    minHeight: 0,
    flexShrink: 1
  },

  uploadButton: {
    width: '100%',
    border: `2px dashed ${COLORS.border}`,
    borderRadius: '12px',
    padding: '40px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: COLORS.backgroundSecondary,
    color: COLORS.textSecondary,
    marginBottom: '24px',
    position: 'relative',
    overflow: 'hidden'
  },

  select: {
    width: '100%',
    backgroundColor: COLORS.background,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '8px',
    padding: '12px 16px',
    color: COLORS.textPrimary,
    fontSize: '14px',
    marginBottom: '20px',
    transition: 'all 0.2s ease'
  },

  pptItem: {
    backgroundColor: COLORS.background,
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    border: `1px solid ${COLORS.border}`,
    transition: 'all 0.2s ease',
    boxShadow: COLORS.shadow
  },

  // 기존 tabButton (호환성 유지)
  tabButton: (active) => ({
    backgroundColor: active ? COLORS.primary : 'transparent',
    color: active ? '#ffffff' : COLORS.textSecondary,
    border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
    borderRadius: '6px',
    padding: isMobile ? '6px 12px' : '8px 16px',
    cursor: 'pointer',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '500',
    margin: '0 4px'
  }),

  // 모바일 햄버거 버튼 (필요시)
  hamburgerButton: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    zIndex: 1001,
    backgroundColor: COLORS.background,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.textPrimary,
    fontSize: '20px',
    cursor: 'pointer',
    padding: '12px',
    borderRadius: '8px',
    display: isMobile ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: COLORS.shadowMd,
    transition: 'all 0.2s ease'
  },

  // 리사이즈 핸들 제거 (사이드바 고정 너비)
  resizeHandle: {
    display: 'none'
  },

  // 닫기 버튼 제거
  closeButton: {
    display: 'none'
  }
});

export const createSlideStyles = (isMobile) => ({
  slideContainer: {
    flex: 1,
    padding: isMobile ? '16px' : '32px',
    backgroundColor: COLORS.backgroundSecondary,
    overflowX: 'auto',
    minHeight: 'calc(100vh - 120px)'
  },

  slideHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    padding: '0'
  },

  slideTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  slideNavigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    marginBottom: 20
  },

  navButton: {
    background: COLORS.background,
    color: COLORS.textPrimary,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '50%',
    width: 48,
    height: 48,
    fontSize: 18,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: COLORS.shadow,
    transition: 'all 0.2s'
  },

  slideContent: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: isMobile ? 12 : 20,
    background: COLORS.background,
    width: isMobile ? '100%' : '100%',
    minHeight: isMobile ? '400px' : '500px',
    maxHeight: isMobile ? '600px' : '700px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    boxShadow: COLORS.shadow
  },

  slideImage: {
    width: '100%',
    height: 'auto',
    maxHeight: '450px',
    borderRadius: 8,
    border: `1px solid ${COLORS.borderLight}`,
    objectFit: 'contain',
    backgroundColor: COLORS.backgroundSecondary,
    flex: '0 0 auto'
  },

  slideText: {
    fontSize: 14,
    color: COLORS.textPrimary,
    lineHeight: '1.6',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    height: '200px',
    overflowY: 'auto',
    padding: '16px',
    backgroundColor: COLORS.backgroundSecondary,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '8px',
    flex: '0 0 auto',
    marginTop: '16px'
  },

  slideIndicators: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '20px',
    flexWrap: 'wrap',
    position: 'relative',
    zIndex: 1
  },

  slideIndicator: (isActive) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: isActive ? COLORS.primary : COLORS.border,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    transform: isActive ? 'scale(1.2)' : 'scale(1)'
  }),

  // 페이지 인디케이터
  pageIndicator: {
    backgroundColor: COLORS.backgroundTertiary,
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '14px',
    color: COLORS.textSecondary,
    fontWeight: '500'
  }
});

// 색상 상수 export (다른 컴포넌트에서 사용)
export { COLORS };
